import React, { useContext, useEffect, useState } from 'react'
import { View, StyleSheet, ScrollView, TouchableOpacity, Platform, Linking } from 'react-native'
import { Text, Button, Modal, Layout, Spinner, Icon, useTheme } from '@ui-kitten/components'
import { UserContext } from '../../global/UserProvider'
import HeaderIconButton from '../../components/HeaderIconButton'
import { logout, updateUserLocale, updateUserStore } from '../../global/Firebase/firebase'
import { useTranslation } from 'react-i18next'
import { useAtomValue, useAtom } from 'jotai'
import { organizationSubscriptionInfoAtom, userLocaleAtom, userRoleAtom, storesDictAtom, userAtom } from '../../global/AtomsProvider'
import PlatformDependantSelect from '../../components/PlatformDependantSelect'
import * as Updates from 'expo-updates'
import { APP_VERSION } from '../../hooks/useVersionCheck'
import { planStatusKey } from '../../utils/pricingPlans'

const SUPPORTED_LANGUAGES = [
  { id: 'ru', text: 'Русский' },
  { id: 'en', text: 'English' }
]

export default function ProfileScreen( { navigation, route } ) {
  const theme = useTheme()
  const { t } = useTranslation()
  const [context, setContext] = useContext(UserContext)
  const [user, setUser] = useAtom(userAtom)
  const userRole = useAtomValue(userRoleAtom)
  const organizationSubscriptionInfo = useAtomValue(organizationSubscriptionInfoAtom)
  const [userLocale, setUserLocale] = useAtom(userLocaleAtom)
  const [userLocaleFieldValue, setUserLocaleFieldValue] = useState()
  const [editUserLocaleModalIsShown, setEditUserLocaleModalIsShown] = useState(false)
  const [editStoreModalIsShown, setEditStoreModalIsShown] = useState(false)
  const [storeFieldValue, setStoreFieldValue] = useState()
  const [isLoading, setIsLoading] = useState(false)
  const storesDict = useAtomValue(storesDictAtom)
  const storeOptions = Object.values(storesDict)
  const [updateIsLoading, setUpdateIsLoading] = useState(false)
  useEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <HeaderIconButton
          onPress={() => {
            logout()
          }}
          iconName='log-out-outline'
        />
      ),
    })
    if (navigation.getState()?.index === 0) {
      navigation.setOptions({
        headerLeft: () => (
          <HeaderIconButton
            onPress={() => {
              navigation.navigate('Home')
            }}
            iconName='arrow-back-outline'
          />
        ),
      })
    }
  }, [])

  const checkForUpdates = async () => {
    if (Platform.OS !== 'web') {
      try {
        setUpdateIsLoading(true)
        const update = await Updates.checkForUpdateAsync()
        if (update.isAvailable) {
          try {
            alert(t('settings.downloadingUpdateHint')) // Assuming t() is available and settings.downloadingUpdateHint exists
            await Updates.fetchUpdateAsync()
            await Updates.reloadAsync()
          } catch(error) {
            console.log(error)
            alert(t('settings.updateError')) // Assuming t() is available and settings.updateError exists
          }
        } else {
          alert(t('settings.noUpdates')) // Assuming t() is available and settings.noUpdates exists
        }
        setUpdateIsLoading(false)
      } catch (e) {
        console.log(e)
        alert(t('settings.updateError')) // Assuming t() is available and settings.updateError exists
        setUpdateIsLoading(false)
      }
    } else {
      alert(t('settings.noUpdateForWebError')) // Assuming t() is available and settings.noUpdateForWebError exists
    }
  }

  return (
    <ScrollView style={styles.container}>
      <Text category='h4' style={styles.text}>{context.user?.name}</Text>
      <Text style={styles.text}>{t('profile.email') + ': ' + context.user?.email}</Text>
      <Text style={styles.text}>{t('organization') + ': ' + organizationSubscriptionInfo?.name}</Text>
      <Text style={styles.text}>{t('role') + ': ' + userRole?.name}</Text>
      <View style={[styles.text, {flexDirection: 'row'}]}>
        <Text>{t('store') + ': ' + context.user?.store?.name}</Text>
        {(userRole?.permissions?.['changeStore'] || context.user?.role === 'admin') && (
          <TouchableOpacity onPress={() => setEditStoreModalIsShown(true)} style={{marginLeft: 5, padding: 2}}>
            <Icon name='edit-outline' height={15} width={15} fill={theme['color-primary-500']} />
          </TouchableOpacity>
        )}
      </View>
      <View style={[styles.text, {flexDirection: 'row'}]}>
        <Text>{t('language') + ': ' + t('languages.' + userLocale)}</Text>
        <TouchableOpacity onPress={() => setEditUserLocaleModalIsShown(true)} style={{marginLeft: 5, padding: 2}}>
          <Icon name='edit-outline' height={15} width={15} fill={theme['color-primary-500']} />
        </TouchableOpacity>
      </View>
      { (userRole?.permissions?.['billing'] || context.user?.role === 'admin') && (
        <>
          <Text style={styles.text}>{t('billing.currentPlan', {currentPlan: organizationSubscriptionInfo?.subscriptionPlanInfo?.text})}</Text>
          <Text style={styles.text}>
            { t('billing.planStatus', {planStatus: t(planStatusKey(organizationSubscriptionInfo))}) }
          </Text>
          <Button style={{marginTop: 10}} onPress={() => navigation.navigate('Billing')}>{t('billingAndPayment')}</Button>
        </>
      )}
      <Button style={{marginTop: 10}} onPress={checkForUpdates} disabled={updateIsLoading}>
        {updateIsLoading ? t('settings.checkingForUpdates') : t('settings.checkForUpdates')} 
      </Button>
      {/* ASO-AN-001: this button used to call `postHog.reset()` itself, immediately before
          `logout()`. That is now done by AnalyticsIdentityBridge, on the auth-state edge, and it
          MUST NOT be done here as well — identity teardown needs exactly one owner.

          Why the duplicate was worse than redundant: `reset()` wipes PostHog's persisted super
          properties (posthog-core nulls every key, `Props` included), including the ATL-531 build
          provenance the read-cutover `ota-coverage` gate reads. The bridge restores them on the
          same edge that clears them. Resetting HERE cleared them with no restore, and then left
          the bridge nothing to do — by the time `onAuthStateChanged(null)` arrived the client was
          already anonymous, so the bridge's transition was `{reset: false}` and its restore never
          ran. Staging QA caught exactly that: `props` went from twelve keys to absent, 250ms after
          the click, and never came back.

          It also only ever covered THIS logout button — four other `logout()` call sites, plus a
          revoked token, reset nothing at all. */}
      <Button style={{marginTop: 10}} status='danger' onPress={() => {
        logout()
      }}>{t('profile.logout')}</Button>
      
      <Modal backdropStyle={styles.backdrop} visible={editUserLocaleModalIsShown}
              onBackdropPress={() => setEditUserLocaleModalIsShown(false)}
      >
        <Layout style={styles.modalContainer}>
          <Text category='label' style={styles.label}>{t('language')}</Text>
          <PlatformDependantSelect
            data={SUPPORTED_LANGUAGES}
            selectedOption={userLocaleFieldValue}
            onSelect={setUserLocaleFieldValue}
            style={{marginTop: 5}}
            withSearch={false}
          />
          {isLoading ? (
            <View style={styles.spinner}><Spinner /></View>
          ) : (
            <>
              <Button style={styles.submitButton} onPress={async () => {
                setIsLoading(true)
                await updateUserLocale(context.user.ref, userLocaleFieldValue?.id)
                // Updating the persisted atom is enough: the locale effect in
                // Routes applies the i18n language and date-fns global locale
                // from it (single source of truth — no divergence).
                setUserLocale(userLocaleFieldValue?.id)
                setIsLoading(false)
                setEditUserLocaleModalIsShown(false)
              }}>
                {t('save')}
              </Button>
              <Button status='basic' style={{ marginTop: 10 }} onPress={() => setEditUserLocaleModalIsShown(false)}>
                {t('cancel')}
              </Button>
            </>
          )}
        </Layout>
      </Modal>

      <Modal backdropStyle={styles.backdrop} visible={editStoreModalIsShown}
              onBackdropPress={() => setEditStoreModalIsShown(false)}
      >
        <Layout style={styles.modalContainer}>
          <Text category='label' style={styles.label}>{t('store')}</Text>
          <PlatformDependantSelect
            data={storeOptions}
            selectedOption={storeFieldValue}
            onSelect={setStoreFieldValue}
            style={{marginTop: 5}}
            withSearch={false}
          />
          {isLoading ? (
            <View style={styles.spinner}><Spinner /></View>
          ) : (
            <>
              <Button style={styles.submitButton} onPress={async () => {
                setIsLoading(true)
                await updateUserStore(context.user.ref, storeFieldValue)
                setContext(prev => ({
                  ...prev,
                  user: {
                    ...prev.user,
                    store: storeFieldValue
                  }
                }))
                setUser(prev => ({
                  ...prev,
                  store: storeFieldValue
                }))
                setIsLoading(false)
                setEditStoreModalIsShown(false)
              }}>
                {t('save')}
              </Button>
              <Button status='basic' style={{ marginTop: 10 }} onPress={() => setEditStoreModalIsShown(false)}>
                {t('cancel')}
              </Button>
            </>
          )}
        </Layout>
      </Modal>

      <Text style={styles.version} appearance='hint' category='c1'>
        {t('settings.version')} {APP_VERSION}
      </Text>
      <View style={{height: 60}}></View>
    </ScrollView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
  }, 
  text: {
    marginBottom: 10,
  },
  submitButton: {
    marginTop: 15,
  },
  modalContainer: {
    justifyContent: 'space-around',
    alignItems: 'stretch',
    width: 320,
    padding: 16,
  },
  backdrop: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  spinner: {
    marginTop: 15,
    alignItems: 'center'
  },
  label: {
    marginBottom: 8,
  },
  version: {
    textAlign: 'center',
    marginTop: 20,
  }
})
